<script lang="ts">
import { defineComponent, inject, onActivated, onDeactivated, PropType, reactive, ref, watch } from "vue";
import Steps from "@/components/Steps.vue";
import { NavvItem } from "@/model/NavItem";
import formatUtils from "@/businesslogic/utils/formatUtils";
import formValidierung from "@/businesslogic/formValidierung";
import AnfrageService from "@/businesslogic/services/AnfrageService";
import Antragsteller from "@/components/person/Antragsteller.vue";
import Zahlungsdaten from "@/components/zahlungsdaten/Zahlungsdaten.vue";
import Vorversicherung from "@/components/vorversicherung/Vorversicherung.vue";
import Vorschaeden from "@/components/vorschaeden/Vorschaeden.vue";
import Modal from "@/components/Modal.vue";
import HugMitgliedschaft from "@/components/hug/HugMitgliedschaft.vue";
import Zelle from "@/components/grid/Zelle.vue";
import Zeile from "@/components/grid/Zeile.vue";
import Ueberschrift from "@/components/grid/Ueberschrift.vue";
import Adresse from "@/components/person/Adresse.vue";
import Logger from "js-logger";
import JaNeinRadio from "@/components/JaNeinRadio.vue";
import ValidierungsFehler from "@/components/ValidierungsFehler.vue";
import TitelZelle from "@/components/grid/TitelZelle.vue";
import VertragsunterlagenDigital from "@/components/abschluss/VertragsunterlagenDigital.vue";
import { useFormStore } from "@/businesslogic/stores/formStore";

export default defineComponent({
  name: "IhreDatenBL",
  components: {
    VertragsunterlagenDigital,
    TitelZelle,
    Steps,
    Ueberschrift,
    Zelle,
    Zeile,
    Adresse,
    Antragsteller,
    Zahlungsdaten,
    Vorversicherung,
    Vorschaeden,
    Modal,
    HugMitgliedschaft,
    JaNeinRadio,
    ValidierungsFehler,
  },
  props: {
    navItems: Object as PropType<NavvItem[]>,
    anfrageId: String,
  },
  emits: ["prev-page", "next-page", "goto-page", "handle-error"],
  setup: function (_props, { emit }) {
    const piniaStore = useFormStore();
    const istSichtbar: any = inject("istSichtbar");
    const wechsleSichtbarkeit: any = inject("wechsleSichtbarkeit");
    const zeigeSpinner: any = inject("zeigeSpinner");

    const { formatCurrency } = formatUtils();
    const fehler = ref({} as any);
    const zeigeFrageHugNachlass = ref(false);
    let komponenteIstAktiv = false;

    const antragstellerRef = ref<InstanceType<typeof Antragsteller>>();
    const zahlungsdatenRef = ref<InstanceType<typeof Zahlungsdaten>>();
    const risikoAdresseRef = ref<InstanceType<typeof Adresse>>();

    const modalBeitragsaenderung = reactive({
      titel: "Beitragsänderung",
      text: "Durch Ihre Änderung wurde der Beitrag angepasst. Bitte überprüfen Sie den neuen Beitrag.",
      hatSchliessenButton: true,
      schliessenButtonText: "OK",
    });
    let beitragsaenderungDurchHugMitgliedschaft = false;

    const eingabenSindValide = async () => {
      const { validiereForm, getFormFehlerAnzahl, getFormFehler, validiereZusammengesetzteNamenLaenge } =
        formValidierung();

      await validiereForm();
      validiereZusammengesetzteNamenLaenge();

      let anstragstellerValide: boolean | undefined = true;
      let risikoAdresseValide: boolean | undefined = true;
      if (getFormFehlerAnzahl() === 0) {
        anstragstellerValide = await antragstellerRef.value?.pruefen();
        risikoAdresseValide = await risikoAdresseRef.value?.pruefen(
          true,
          (verlaesslich) => (piniaStore.state.risikoAnschrift.wurdeGeprueft = verlaesslich),
        );
      }

      Logger.debug(
        "Anzahl Fehler aus Validierung: " + getFormFehlerAnzahl(),
        risikoAdresseValide,
        anstragstellerValide,
        getFormFehler(),
      );
      fehler.value = getFormFehler();

      return risikoAdresseValide && anstragstellerValide && getFormFehlerAnzahl() === 0;
    };

    const anfragePruefenUndWeiter = () => {
      zeigeSpinner.value = true;
      AnfrageService.pruefen(piniaStore.getSessionToken(piniaStore.state), piniaStore.state, "IhreDatenBL")
        .then((res) => {
          Logger.debug("anfragePruefenUndWeiter(): OK. Ergebnis: ", res.data);
          if (res.data && res.data.aussteuerung !== undefined) {
            piniaStore.state.isDunkelverarbeitung = res.data.aussteuerung === false;
          }
        })
        .catch((err) => {
          Logger.debug("anfragePruefenUndWeiter(): Fehler:", err);
        })
        .then(function () {
          zeigeSpinner.value = false;
          emit("next-page", { pageIndex: 2 });
        });
    };

    const nextPage = () => {
      eingabenSindValide().then((isValid) => {
        if (isValid) {
          anfragePruefenUndWeiter();
        }
      });
    };

    watch(
      () => piniaStore.state.tarifinformationen.zahlungsweise,
      (neu, alt) => {
        if (komponenteIstAktiv && neu !== alt) {
          Logger.debug("WATCH (IhreDaten>zahlungsweise): ZW hat sich geändert.");
          if (
            Number(piniaStore.state.tarifinformationen.zahlungsweise) > 1 &&
            piniaStore.state.zahlungsdaten.zahlungsart !== "debit"
          ) {
            Logger.debug("WATCH (IhreDaten>zahlungsweise): Auf Lastschrift umstellen...");
            piniaStore.state.zahlungsdaten.zahlungsart = "debit";
          }
        }
      },
    );

    watch(
      () => piniaStore.state.preise.produktLinien[piniaStore.state.preise.produktLinienIndex].einmalig,
      (beitragNeu, beitragAlt) => {
        Logger.debug("WATCH (IhreDaten>zahlbeitrag):\nbeitragNeu:" + beitragNeu + "\nbeitragAlt:" + beitragAlt);
        if (
          komponenteIstAktiv &&
          beitragAlt > 0 &&
          beitragNeu !== beitragAlt &&
          beitragsaenderungDurchHugMitgliedschaft
        ) {
          Logger.debug("WATCH (IhreDaten>zahlbeitrag): Beitrag hat sich geändert.");
          if (beitragsaenderungDurchHugMitgliedschaft) {
            modalBeitragsaenderung.titel = "Mitgliedschaft Haus & Grund-Verein";
            modalBeitragsaenderung.text = "Durch die Änderung der Mitgliedschaft in einem Haus & Grund-Verein";
            beitragsaenderungDurchHugMitgliedschaft = false;
          }
          modalBeitragsaenderung.text +=
            " ändert sich Ihr Beitrag von " + formatCurrency(beitragAlt) + " auf " + formatCurrency(beitragNeu);
          Logger.debug(modalBeitragsaenderung.text);
          wechsleSichtbarkeit("modalBeitragsaenderung");
        }
      },
    );

    watch(
      () => piniaStore.state.tarifinformationen.hugMitglied,
      (wertNeu, wertAlt) => {
        if (wertNeu !== wertAlt) {
          zeigeFrageHugNachlass.value = piniaStore.state.tarifinformationen.hugMitglied;
          beitragsaenderungDurchHugMitgliedschaft = true;
        }
      },
    );

    onActivated(() => {
      komponenteIstAktiv = true;
      zeigeFrageHugNachlass.value = piniaStore.state.tarifinformationen.hugMitglied;
    });

    onDeactivated(() => {
      komponenteIstAktiv = false;
    });

    return {
      piniaStore,
      istSichtbar,
      wechsleSichtbarkeit,
      zeigeSpinner,
      formatCurrency,
      fehler,
      zeigeFrageHugNachlass,
      nextPage,
      modalBeitragsaenderung,
      antragstellerRef,
      zahlungsdatenRef,
      risikoAdresseRef,
    };
  },
});
</script>

<template>
  <steps :active="2" v-bind:items="navItems" @goto-page="$emit('goto-page', $event)" />
  <section>
    <ueberschrift titel="Versichertes Gebäude" />
    <adresse
      :fehler="fehler"
      :placeholder="true"
      v-model:plz="piniaStore.state.risikoAnschrift.plz"
      v-model:ort="piniaStore.state.risikoAnschrift.ort"
      v-model:strasse="piniaStore.state.risikoAnschrift.strasse"
      v-model:hausnummer="piniaStore.state.risikoAnschrift.hausnummer"
      :feldNamen="{
        plz: 'risikoPlz',
        ort: 'risikoOrt',
        strasse: 'risikoStrasse',
        hausnummer: 'risikoHausnummer',
      }"
      ref="risikoAdresseRef"
    >
      <template v-slot:plzOrtLabel>PLZ und Ort</template>
      <template v-slot:strasseHausnummerLabel>Straße und Hausnummer</template>
    </adresse>

    <zeile>
      <titel-zelle>Sind Sie Alleineigentümer des Gebäudes</titel-zelle>
      <zelle>
        <ja-nein-radio name="alleineigentuemer" v-model="piniaStore.state.gebaeudedaten.alleineigentuemer" />
        <validierungs-fehler :fehler-text="fehler.alleineigentuemer" />
      </zelle>
    </zeile>

    <zeile v-if="piniaStore.state.gebaeudedaten.alleineigentuemer === false">
      <titel-zelle>Art der Miteigentümerschaft</titel-zelle>
      <zelle>
        <select
          name="miteigentuemerTyp"
          v-model="piniaStore.state.gebaeudedaten.miteigentuemerTyp"
          class="c-form-control-select"
          data-cy="miteigentuemerTyp"
          data-fehlermeldung="Bitte wählen Sie eine Art der Miteigentümerschaft aus."
          required
          aria-label="Art der Miteigentümerschaft"
          aria-required="true"
        >
          <option value="">Bitte wählen</option>
          <option value="EHE">Ehe-/Lebenspartnerschaft</option>
          <option value="ETG">Eigentümergemeinschaft (EG)</option>
          <option value="WEG">Wohnungseigentümergemeinschaft (WEG)</option>
        </select>
        <validierungs-fehler :fehler-text="fehler.miteigentuemerTyp" :reduzierter-abstand="true" />
      </zelle>
    </zeile>

    <antragsteller :fehler="fehler" ref="antragstellerRef" />

    <vertragsunterlagen-digital :fehler="fehler" />

    <zahlungsdaten :fehler="fehler" ref="zahlungsdatenRef" />

    <vorversicherung :fehler="fehler" />

    <vorschaeden :fehler="fehler" />

    <hug-mitgliedschaft v-if="zeigeFrageHugNachlass" :fehler="fehler" />

    <!-- Absenden -->
    <zeile>
      <zelle :breite="4" :offset="4">
        <button
          class="c-button c-button--full-width u-mar-t-2 u-mar-b-2 mobil-mehrAbstand"
          data-cy="submitIhreDaten"
          @click="nextPage()"
        >
          Zusammenfassung und Abschluss
        </button>
      </zelle>
    </zeile>

    <modal
      v-if="istSichtbar('modalBeitragsaenderung')"
      v-bind="modalBeitragsaenderung"
      @closeModal="wechsleSichtbarkeit('modalBeitragsaenderung')"
    />
  </section>
</template>
